import React, { useEffect, useState } from 'react'

import styled from 'styled-components'

import {
  Alert,
  Box,
  Button,
  Flex,
  LegacySpinner,
  Spinner,
} from 'app/components/design-system-components'
import HorizontalTabs from 'app/components/design-system-components/HorizontalTabs'
import * as Display from 'app/components/display/display'
import { theme } from 'app/match/applicationReportPDF/assets/theme'
import * as snugNotifier from 'app/services/snugNotifier'
import MembersList from 'app/sm/team_viewing_preference/components/members_list'
import {
  deleteTeamMember,
  getTeamMembersList,
  updateTeamMember,
} from 'app/sm/team_viewing_preference/components/members_list/api'
import ChangeRoleModal from 'app/sm/team_viewing_preference/components/members_list/changeRoleModal'
import EditReferrerIdModal from 'app/sm/team_viewing_preference/components/members_list/EditReferrerIdModal'
import RemoveTeamMemberModal from 'app/sm/team_viewing_preference/components/members_list/removeTeamMemberModal'
import { getSuggestedTeamMembersList } from 'app/sm/team_viewing_preference/setup/api'
import InviteTeamMembersModal from 'app/sm/team_viewing_preference/setup/inviteTeamMemberModal'
import TeamList from 'app/sm/team_viewing_preference/setup/letsGetStarted/TeamList'
import { useLoadingStates } from 'app/utils/hooks/useLoadingStates'
import { loadingStatesIds } from 'app/utils/loading-states'

const StyledIntro = styled(Box)`
  line-height: ${({ theme }) => theme.lineHeights.title};
  margin-top: ${({ theme }) => theme.space[5]}px;
  margin-bottom: ${({ theme }) => theme.space[7]}px;
`

const AdminActions = styled(Flex)`
  gap: ${({ theme }) => theme.space[5]}px;
  margin-bottom: ${({ theme }) => theme.space[5]}px;
  align-items: center;
`

function separateMembersAndInvites(allMembers) {
  const members = []
  const invites = []
  for (const member of allMembers) {
    if (member.status === 'invited') {
      invites.push(member)
    }
    if (member.status === 'accepted') {
      members.push(member)
    }
  }
  return { members, invites }
}

function TeamMembers({
  teamGUID,
  isAdmin,
  invite = {},
  hideSuggestedMembers = true,
  currentTeam,
  onNavigateToSupportAccess,
}) {
  const {
    loadingStates: teamMembersLoadingStates,
    loadingStatesHelpers: teamMembersLoadingStateHelpers,
  } = useLoadingStates()

  const {
    loadingStates: suggestedTeamMembersLoadingStates,
    loadingStatesHelpers: suggestedTeamMembersLoadingStateHelpers,
  } = useLoadingStates()

  const [activeTabGUID, setActiveTabGUID] = useState('1')
  const onClickTabInHorizontalTab = (tabGUID) => {
    setActiveTabGUID(tabGUID)
  }

  const [removeTeamMemberDetails, setRemoveTeamMemberDetails] = useState({
    modalOpen: false,
    name: '',
    guid: '',
    email: '',
  })

  const [changeRoleDetails, setChangeRoleDetails] = useState({
    modalOpen: false,
    name: '',
    guid: '',
    roles: {},
  })

  const [modifiedInvite, setModifiedInvite] = useState({
    name: '',
    email: '',
  })
  const [inviteTeamMembersModalOpen, setInviteTeamMembersModal] =
    useState(false)
  const [teamMembersList, setTeamMembersList] = useState([])
  const [teamInvitesList, setTeamInvitesList] = useState([])
  const [suggestedTeamMembersList, setSuggestedTeamMembersList] = useState([])
  const [editReferrerIdModalOpen, setEditReferrerIdModalOpen] = useState(false)
  const [initialReferrerIds, setInitialReferrerIds] = useState({
    agentUtilityProviderUserID: '',
    userId: '',
  })
  const fetchTeamMembers = () => {
    teamMembersLoadingStateHelpers.startLoading()
    getTeamMembersList(teamGUID)
      .then(({ data }) => {
        const { members, invites } = separateMembersAndInvites(data)
        setTeamMembersList(members)
        setTeamInvitesList(invites)
        teamMembersLoadingStateHelpers.markDoneSuccessfully()
      })
      .catch((err) => {
        teamMembersLoadingStateHelpers.setError(err)
      })
  }
  useEffect(() => {
    fetchTeamMembers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    teamMembersLoadingStateHelpers.startLoading()
    suggestedTeamMembersLoadingStateHelpers.startLoading()

    getSuggestedTeamMembersList(teamGUID)
      .then((data) => {
        setSuggestedTeamMembersList(data)
        suggestedTeamMembersLoadingStateHelpers.markDoneSuccessfully()
      })
      .catch((error) => suggestedTeamMembersLoadingStateHelpers.setError(error))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (invite && invite.isShown) {
      setInviteTeamMembersModal(true)
      const { inviteData = {} } = invite
      const {
        firstName: invitedFirstName = '',
        lastName: invitedLastName = '',
        email: invitedEmail = '',
      } = inviteData
      setModifiedInvite({
        name: invitedFirstName + invitedLastName,
        email: invitedEmail,
      })
    }
  }, [invite])

  const onClickRemoveHandler = (item) => {
    const { firstName = '', lastName = '', guid = '', email = '' } = item
    setRemoveTeamMemberDetails({
      name: `${firstName} ${lastName}`,
      guid,
      modalOpen: true,
      email,
    })
  }

  const onClickConfirmRemoveTeamMember = (deleteUser) => {
    const payload = {
      email: removeTeamMemberDetails.email,
      teamMemberGUID: removeTeamMemberDetails.guid,
      deleteUser,
    }
    deleteTeamMember(teamGUID, payload)
      .then(() => {
        if (fetchTeamMembers) {
          fetchTeamMembers()
        }
        toggleRemoveTeamMemberModal()
        snugNotifier.success('Team Member successfully removed')
      })
      .catch((error) => snugNotifier.error(error.message))
  }

  const onClickChangeRoleHandler = (changeRoleUser) => {
    const {
      firstName = '',
      lastName = '',
      guid = '',
      isAdmin = false,
      isTeamLeader = false,
      isLeasingConsultant = false,
      isPropertyManager = false,
      isCasualInspector = false,
      isBDM = false,
      isFinance = false,
    } = changeRoleUser

    setChangeRoleDetails({
      modalOpen: true,
      name: `${firstName} ${lastName}`,
      guid,
      isAdmin,
      isTeamLeader,
      isLeasingConsultant,
      isPropertyManager,
      isCasualInspector,
      isBDM,
      isFinance,
    })
  }

  const toggleEditReferrerIdModal = (item) => {
    const { agentUtilityProviderUserID = '', guid = '' } = item || {}
    if (item && !editReferrerIdModalOpen) {
      setInitialReferrerIds({ agentUtilityProviderUserID, userId: guid })
    } else {
      setInitialReferrerIds({ agentUtilityProviderUserID: '', userId: '' })
      fetchTeamMembers()
    }
    setEditReferrerIdModalOpen(!editReferrerIdModalOpen)
  }

  const onClickUpdateTeamMember = (changeRole) => {
    const payload = {
      isAdmin: changeRole.isAdmin,
      isTeamLeader: changeRole.isTeamLeader,
      isLeasingConsultant: changeRole.isLeasingConsultant,
      isPropertyManager: changeRole.isPropertyManager,
      isCasualInspector: changeRole.isCasualInspector,
      isBDM: changeRole.isBDM,
      isFinance: changeRole.isFinance,
    }
    updateTeamMember(teamGUID, changeRoleDetails.guid, payload)
      .then(() => {
        if (fetchTeamMembers) {
          fetchTeamMembers()
        }
        toggleChangeRoleModal()
        snugNotifier.success('Team Member successfully updated')
      })
      .catch((error) => snugNotifier.error(error.message))
  }

  const toggleRemoveTeamMemberModal = () => {
    setRemoveTeamMemberDetails({
      ...removeTeamMemberDetails,
      modalOpen: !removeTeamMemberDetails.modalOpen,
    })
  }

  const toggleChangeRoleModal = () => {
    setChangeRoleDetails({
      ...changeRoleDetails,
      modalOpen: !changeRoleDetails.modalOpen,
    })
  }

  const toggleInviteTeamMembersModal = () => {
    setInviteTeamMembersModal(!inviteTeamMembersModalOpen)
  }

  const memberTools = [
    {
      text: 'Change Role',
      enableTool: true,
      onClick: onClickChangeRoleHandler,
    },
    {
      text: 'Edit UCS Referrer ID',
      enableTool: isAdmin,
      onClick: toggleEditReferrerIdModal,
    },
    {
      text: 'Remove',
      enableTool: true,
      danger: true,
      onClick: onClickRemoveHandler,
    },
  ]

  const onClickInvite = (member) => {
    toggleInviteTeamMembersModal()
    setModifiedInvite(member)
  }

  const { providerName = '' } = currentTeam || {}

  return (
    <div>
      <Flex alignItems="center">
        <Display.MainHeader
          hasSeperator={false}
          text="Team Members"
          textClass="p0"
          componentClass="mb0"
        />
      </Flex>

      <Display.DefaultBodyText>
        <StyledIntro>
          Manage your team members, view activity and invite new team members.{' '}
          <a href="mailto:help@snug.com">learn more.</a>
        </StyledIntro>

        <>
          {teamMembersLoadingStates.state !== loadingStatesIds.ERROR ? (
            <>
              {isAdmin && (
                <AdminActions>
                  <Button
                    sizeVariant="large"
                    onClick={() => toggleInviteTeamMembersModal()}
                  >
                    Invite New Team Member
                  </Button>
                  {onNavigateToSupportAccess && (
                    <Button
                      sizeVariant="large"
                      onClick={onNavigateToSupportAccess}
                    >
                      Manage Snug Support Access
                    </Button>
                  )}
                </AdminActions>
              )}
              <HorizontalTabs
                tabHeaders={[
                  { tabGUID: '1', label: 'Members' },
                  { tabGUID: '2', label: 'Invites' },
                  {
                    tabGUID: '3',
                    label: 'Suggested',
                    hidden: hideSuggestedMembers,
                  },
                ]}
                activeTabGUID={activeTabGUID}
                onClickTabInHorizontalTab={onClickTabInHorizontalTab}
              />
              <Box>
                {activeTabGUID === '1' && (
                  <>
                    {teamMembersLoadingStates.state ===
                    loadingStatesIds.LOADING ? (
                      <Flex justifyContent="center">
                        <Spinner />
                      </Flex>
                    ) : (
                      <>
                        {teamMembersLoadingStates.state !==
                        loadingStatesIds.ERROR ? (
                          <MembersList
                            teamMembersList={teamMembersList}
                            showToolsButton={isAdmin}
                            memberTools={memberTools}
                            onClickHandler={onClickRemoveHandler}
                            showAvatar={false}
                          />
                        ) : (
                          <>
                            <Alert
                              variant="warningWithBg"
                              mt={theme.space[3] + 'px'}
                            >
                              <div>
                                {teamMembersLoadingStates.error.message}
                              </div>
                            </Alert>
                          </>
                        )}
                      </>
                    )}
                  </>
                )}
                {activeTabGUID === '2' && (
                  <>
                    {teamMembersLoadingStates.state ===
                    loadingStatesIds.LOADING ? (
                      <Flex justifyContent="center">
                        <Spinner />
                      </Flex>
                    ) : (
                      <>
                        {teamMembersLoadingStates.state !==
                        loadingStatesIds.ERROR ? (
                          <MembersList
                            teamMembersList={teamInvitesList}
                            showToolsButton={isAdmin}
                            memberTools={memberTools}
                            onClickHandler={onClickRemoveHandler}
                            showAvatar={false}
                          />
                        ) : (
                          <>
                            <Alert
                              variant="warningWithBg"
                              mt={theme.space[3] + 'px'}
                            >
                              <div>
                                {teamMembersLoadingStates.error.message}
                              </div>
                            </Alert>
                          </>
                        )}
                      </>
                    )}
                  </>
                )}
                {activeTabGUID === '3' && (
                  <div>
                    {suggestedTeamMembersLoadingStates.state ===
                    loadingStatesIds.LOADING ? (
                      <Flex justifyContent="center">
                        <LegacySpinner />
                      </Flex>
                    ) : (
                      <>
                        {suggestedTeamMembersLoadingStates.state !==
                        loadingStatesIds.ERROR ? (
                          <>
                            {suggestedTeamMembersList &&
                            suggestedTeamMembersList.length > 0 ? (
                              <TeamList
                                membersList={suggestedTeamMembersList}
                                showInviteButtons={true}
                                onClickInvite={onClickInvite}
                              />
                            ) : (
                              <Alert>
                                Could not find any team members. Please invite
                                team members by clicking the button above.
                              </Alert>
                            )}
                          </>
                        ) : (
                          <>
                            <Alert
                              variant="warningWithBg"
                              mt={theme.space[6] + 'px'}
                            >
                              <div>
                                {
                                  suggestedTeamMembersLoadingStates.error
                                    .message
                                }
                              </div>
                            </Alert>
                          </>
                        )}
                      </>
                    )}
                  </div>
                )}
              </Box>
            </>
          ) : (
            <Alert variant="warningWithBg" mt={theme.space[3] + 'px'}>
              <div>{teamMembersLoadingStates.error.message}</div>
            </Alert>
          )}
        </>
        {inviteTeamMembersModalOpen && (
          <InviteTeamMembersModal
            toggleModal={toggleInviteTeamMembersModal}
            teamGUID={teamGUID}
            initialInvite={modifiedInvite}
            onSuccessfulInvite={fetchTeamMembers}
          />
        )}
        {removeTeamMemberDetails.modalOpen && (
          <RemoveTeamMemberModal
            toggleModal={toggleRemoveTeamMemberModal}
            name={removeTeamMemberDetails.name}
            email={removeTeamMemberDetails.email}
            onClickConfirmRemoveTeamMember={onClickConfirmRemoveTeamMember}
          />
        )}
        {changeRoleDetails.modalOpen && (
          <ChangeRoleModal
            toggleModal={toggleChangeRoleModal}
            name={changeRoleDetails.name}
            initialRoles={{
              isAdmin: changeRoleDetails.isAdmin,
              isTeamLeader: changeRoleDetails.isTeamLeader,
              isLeasingConsultant: changeRoleDetails.isLeasingConsultant,
              isPropertyManager: changeRoleDetails.isPropertyManager,
              isCasualInspector: changeRoleDetails.isCasualInspector,
              isBDM: changeRoleDetails.isBDM,
              isFinance: changeRoleDetails.isFinance,
            }}
            onClickUpdateTeamMember={onClickUpdateTeamMember}
          />
        )}
        {editReferrerIdModalOpen && (
          <EditReferrerIdModal
            toggleModal={toggleEditReferrerIdModal}
            agencyId={teamGUID}
            currentReferrerId={
              initialReferrerIds?.agentUtilityProviderUserID || ''
            }
            userId={initialReferrerIds?.userId || ''}
            providerName={providerName}
            sendUtilityReferralToLeadAfterEditingID={false}
          />
        )}
      </Display.DefaultBodyText>
    </div>
  )
}

export default TeamMembers
